import { useRef, useEffect, Suspense, lazy } from "react";
import "@fontsource-variable/inter";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dates/styles.css";
import {
  AppShell,
  Container,
  Loader,
  MantineProvider,
  createTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { Route, Switch } from "wouter";
import { ErrorBoundary } from "@sentry/react";
import ErrorFallback from "./_Common/ErrorFallback.jsx";
import NavBar from "./NavBar.jsx";
import { useStore } from "./store";
import { getProfile, getProviders } from "./api";
import AppHeader from "./AppHeader.jsx";
import ProfileEdit from "./Profile/ProfileEdit.jsx";
import PaymentCompleted from "./Payment/Completed.jsx";
import PaymentCancelled from "./Payment/Cancelled.jsx";
import PlaygroundThreadPage from "./Playground/Thread/PlaygroundThreadPage.jsx";
import PlaygroundBulkPage from "./Playground/Bulk/PlaygroundBulkPage.jsx";
import InvocationLogPage from "./InvocationLog/InvocationLogPage.jsx";
import ForgotPassword from "./Auth/ForgotPassword.jsx";
import ResetPassword from "./Auth/ResetPassword.jsx";
import PromptList from "./Playground/Prompts/PromptList.jsx";
import PromptDetails from "./Playground/Prompts/PromptDetails.jsx";
import Home from "./Home.jsx";
import PromptEdit from "./Playground/Prompts/PromptEdit.jsx";
import FlowCreate from "./Flows/FlowCreate.jsx";
import FlowDetails from "./Flows/FlowDetails.jsx";
import FlowList from "./Flows/FlowList.jsx";
import FlowRun from "./Flows/FlowRun.jsx";
import UserList from "./Users/UserList.jsx";
import UserEdit from "./Users/UserEdit.jsx";
import TenantProfileEdit from "./Tenants/TenantProfileEdit.jsx";
import UserAdd from "./Users/UserAdd.jsx";
import InvitationForm from "./Auth/Invitation.jsx";
import "./global.css";
import Environments from "./Environments/Environments.jsx";
import EnvironmentPromote from "./Environments/EnvironmentPromote.jsx";
import ToolList from "./Tools/ToolList.jsx";
import ToolCreate from "./Tools/ToolCreate.jsx";
import ToolEdit from "./Tools/ToolEdit.jsx";
import McpServerList from "./McpServers/McpServerList.jsx";
import McpServerCreate from "./McpServers/McpServerCreate.jsx";
import McpServerEdit from "./McpServers/McpServerEdit.jsx";
import PanelChatPage from "./Panels/PanelChatPage.jsx";
import PanelList from "./Panels/PanelList.jsx";
import PanelEditor from "./Panels/PanelEditor.jsx";
import FeedbackPage from "./Feedback/FeedbackPage.jsx";

// TBD: Lazy-load these components to improve initial load time
import Confirmation from "./Auth/Confirmation.jsx";
import LoginForm from "./Auth/Login.jsx";
import LoginLink from "./Auth/LoginLink.jsx";
import RegisterForm from "./Auth/Register.jsx";

import TenantList from "./_Boilerplate/AdminTenants/TenantList.jsx";
import TenantEdit from "./_Boilerplate/AdminTenants/TenantEdit.jsx";
import TenantCreate from "./_Boilerplate/AdminTenants/TenantCreate.jsx";

const ModelList = lazy(() => import("./ModelList/ModelList"));
const ModelDetails = lazy(() => import("./ModelList/ModelDetails"));
const Dashboard = lazy(() => import("./Dashboard/Dashboard"));
const AccountOverview = lazy(() => import("./Dashboard/AccountOverview"));
const TagCostReport = lazy(() => import("./Dashboard/TagCostReport"));
const ProviderCostOverview = lazy(() =>
  import("./Dashboard/ProviderCostOverview")
);

const AdminUsersList = lazy(() =>
  import("./_Boilerplate/AdminUsers/AdminUsersList")
);
const AdminUserDetails = lazy(() =>
  import("./_Boilerplate/AdminUsers/AdminUserDetails")
);

const JobListAdmin = lazy(() => import("./_Boilerplate/AdminJobs/Jobs"));
const JobDetailsAdmin = lazy(() =>
  import("./_Boilerplate/AdminJobs/JobDetails")
);

const baseUrl = import.meta.env.BASE_URL;

function PageLoader() {
  return (
    <Container size="sm" style={{ textAlign: "center", padding: "2rem" }}>
      <Loader size="xl" />
    </Container>
  );
}

function App() {
  const [opened, { toggle }] = useDisclosure();
  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const initialized = useRef(false);

  const setProviders = useStore((state) => state.setProviders);

  useEffect(() => {
    getProviders().then((data) => {
      setProviders(data);
    });
    if (authToken && !profile) {
      if (!initialized.current) {
        initialized.current = true;
        getProfile()
          .then((data) => {
            setProfile(data);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }
    // getProviders() runs unconditionally in this effect; adding `profile` would re-fetch providers
    // on every profile update. `setProfile`/`setProviders` are stable Zustand actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const theme = createTheme({
    primaryColor: "shuttle-navy",
    colors: {
      "shuttle-amber": [
        "#FFF8E1", // 0
        "#FFECB3", // 1
        "#FFE082", // 2
        "#FFD54F", // 3
        "#FFCA28", // 4
        "#FFC107", // 5
        "#F59E0B", // 6 - primary
        "#E68A00", // 7
        "#CC7A00", // 8
        "#A66200", // 9
      ],
      "shuttle-navy": [
        "#E8EDF5", // 0
        "#C4CDE0", // 1
        "#9FAECA", // 2
        "#7A8EB5", // 3
        "#556FA0", // 4
        "#3B5280", // 5
        "#263B66", // 6
        "#1A2D52", // 7
        "#0F1F3D", // 8
        "#0F1729", // 9
      ],
    },
    fontFamily:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyMonospace:
      '"JetBrains Mono", ui-monospace, "Cascadia Mono", monospace',
    headings: {
      fontFamily:
        '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: "700",
    },
    defaultRadius: "md",
    spacing: {
      xs: "8px",
      sm: "10px",
      md: "14px",
      lg: "18px",
      xl: "22px",
    },
    components: {
      Container: { defaultProps: { size: "lg" } },
      Button: { defaultProps: { size: "sm" } },
      TextInput: { defaultProps: { size: "sm" } },
      PasswordInput: { defaultProps: { size: "sm" } },
      Select: { defaultProps: { size: "sm" } },
      Textarea: { defaultProps: { size: "sm" } },
      NumberInput: { defaultProps: { size: "sm" } },
      MultiSelect: { defaultProps: { size: "sm" } },
      Tabs: { defaultProps: { variant: "outline" } },
      Badge: { defaultProps: { size: "sm" } },
      Table: { defaultProps: { highlightOnHover: true, striped: true } },
    },
  });

  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <ModalsProvider>
        <Notifications />
        <AppShell
          header={{ height: 48 }}
          navbar={{
            width: 190,
            breakpoint: "sm",
            collapsed: { mobile: !opened },
          }}
          padding="sm"
        >
          <AppShell.Header>
            <ErrorBoundary fallback={ErrorFallback}>
              <AppHeader toggle={toggle} opened={opened} />
            </ErrorBoundary>
          </AppShell.Header>
          <AppShell.Navbar p={0}>
            <ErrorBoundary fallback={ErrorFallback}>
              <NavBar toggle={toggle} />
            </ErrorBoundary>
          </AppShell.Navbar>
          <AppShell.Main>
            <ErrorBoundary fallback={ErrorFallback}>
              <Suspense fallback={<PageLoader />}>
                <Switch>
                  <Route path={baseUrl} component={LoginForm} />
                  <Route path={`${baseUrl}dashboard`} component={Dashboard} />
                  <Route path={`${baseUrl}usage`} component={AccountOverview} />
                  <Route path={`${baseUrl}tag-costs`} component={TagCostReport} />
                  <Route path={`${baseUrl}models`} component={ModelList} />
                  <Route
                    path={`${baseUrl}models/:modelName`}
                    component={ModelDetails}
                  />

                  <Route
                    path={`${baseUrl}auth/register`}
                    component={RegisterForm}
                  />
                  <Route
                    path={`${baseUrl}invite/:token`}
                    component={InvitationForm}
                  />
                  <Route
                    path={`${baseUrl}auth/reset`}
                    component={ForgotPassword}
                  />
                  <Route
                    path={`${baseUrl}auth/reset-password/:token`}
                    component={ResetPassword}
                  />
                  <Route
                    path={`${baseUrl}auth/login/:token`}
                    component={LoginLink}
                  />
                  <Route path={`${baseUrl}auth/login`} component={LoginForm} />
                  <Route path={`${baseUrl}profile`} component={ProfileEdit} />
                  <Route
                    path={`${baseUrl}tenant/profile`}
                    component={TenantProfileEdit}
                  />
                  <Route
                    path={`${baseUrl}auth/confirm-email/:token`}
                    component={Confirmation}
                  />

                  <Route
                    path={`${baseUrl}payment/completed`}
                    component={PaymentCompleted}
                  />
                  <Route
                    path={`${baseUrl}payment/cancelled`}
                    component={PaymentCancelled}
                  />

                  <Route path={`${baseUrl}home`} component={Home} />

                  {/* <Route
                path={`${baseUrl}verbatim`}
                component={PlaygroundVerbatimPage}
              /> */}

                  <Route path={`${baseUrl}prompts`} component={PromptList} />
                  <Route
                    path={`${baseUrl}prompts/edit/:id`}
                    component={PromptEdit}
                  />
                  <Route
                    path={`${baseUrl}prompts/:id`}
                    component={PromptDetails}
                  />

                  <Route path={`${baseUrl}users`} component={UserList} />
                  <Route path={`${baseUrl}users/add`} component={UserAdd} />
                  <Route
                    path={`${baseUrl}users/edit/:id`}
                    component={UserEdit}
                  />
                  {/* <Route path={`${baseUrl}users/:id`} component={UserDetails} /> */}

                  <Route
                    path={`${baseUrl}environments/promote`}
                    component={EnvironmentPromote}
                  />
                  <Route
                    path={`${baseUrl}environments`}
                    component={Environments}
                  />

                  <Route
                    path={`${baseUrl}chat`}
                    component={PlaygroundThreadPage}
                  />
                  <Route
                    path={`${baseUrl}bulk`}
                    component={PlaygroundBulkPage}
                  />

                  <Route path={`${baseUrl}flows`} component={FlowList} />
                  <Route
                    path={`${baseUrl}flows/create`}
                    component={FlowCreate}
                  />
                  <Route
                    path={`${baseUrl}flows/:flowId`}
                    component={FlowDetails}
                  />
                  <Route
                    path={`${baseUrl}flows/:flowId/run`}
                    component={FlowRun}
                  />

                  <Route
                    path={`${baseUrl}logs`}
                    component={InvocationLogPage}
                  />
                  <Route
                    path={`${baseUrl}logs/:id`}
                    component={InvocationLogPage}
                  />

                  <Route
                    path={`${baseUrl}feedback`}
                    component={FeedbackPage}
                  />

                  <Route path="/tools" component={ToolList} />
                  <Route path="/tools/new" component={ToolCreate} />
                  <Route path="/tools/:toolId" component={ToolEdit} />

                  <Route path="/mcp-servers" component={McpServerList} />
                  <Route path="/mcp-servers/new" component={McpServerCreate} />
                  <Route
                    path="/mcp-servers/:serverId"
                    component={McpServerEdit}
                  />

                  <Route path="/panels/manage/new" component={PanelEditor} />
                  <Route
                    path="/panels/manage/:panelId"
                    component={PanelEditor}
                  />
                  <Route path="/panels/manage" component={PanelList} />
                  <Route path="/panels" component={PanelChatPage} />

                  {/* ADMIN STUFF, BOILERPLATE */}
                  {profile?.userRole == "GlobalAdmin" && (
                    <>
                      <Route
                        path="/admin/provider-costs"
                        component={ProviderCostOverview}
                      />
                      <Route path="/admin/jobs" component={JobListAdmin} />
                      <Route
                        path="/admin/jobs/:id"
                        component={JobDetailsAdmin}
                      />

                      <Route path={`/tenants`} component={TenantList} />
                      <Route
                        path={`/tenants/create`}
                        component={TenantCreate}
                      />
                      <Route path={`/tenants/:id`} component={TenantEdit} />

                      {/* <Route
                      path={`/tenants/:tenantId/users/create`}
                      component={TenantUserCreate}
                    /> */}
                      <Route path={`/admin/users`} component={AdminUsersList} />
                      <Route
                        path={`/admin/users/:userId`}
                        component={AdminUserDetails}
                      />
                    </>
                  )}

                  <Route>404, Not Found!</Route>
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </AppShell.Main>
        </AppShell>
      </ModalsProvider>
    </MantineProvider>
  );
}

export default App;
